import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  HStack,
  Heading,
  Progress,
  Spinner,
  Stack,
  Stat,
  Table,
  Text,
} from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import {
  FiAlertCircle,
  FiBarChart2,
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi"

import {
  useCapacityReport,
  useDashboardSummary,
  useEnrollmentReport,
  useOutstandingPayments,
  useRevenueReport,
} from "@/hooks/api/useReports"
import { usePermissions } from "@/hooks/usePermissions"

export const Route = createFileRoute("/_layout/reports")({
  component: ReportsPage,
})

function ReportsPage() {
  const { can } = usePermissions()
  const navigate = useNavigate()
  const canViewReports =
    can("reports:read") || can("reports:read_financial") || can("system:admin")

  const [activeTab, setActiveTab] = useState<
    "summary" | "revenue" | "enrollments" | "payments" | "capacity"
  >("summary")

  // Fetch data for all tabs
  const { data: summary, isLoading: summaryLoading } = useDashboardSummary()
  const { data: revenue, isLoading: revenueLoading } = useRevenueReport()
  const { data: enrollments, isLoading: enrollmentsLoading } =
    useEnrollmentReport()
  const { data: outstanding, isLoading: outstandingLoading } =
    useOutstandingPayments()
  const { data: capacity, isLoading: capacityLoading } = useCapacityReport()

  if (!canViewReports) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>You don't have permission to view reports.</Text>
      </Container>
    )
  }

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return "$0.00"
    return `$${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: "red",
      partial: "orange",
      paid: "green",
      confirmed: "green",
      waitlisted: "yellow",
      cancelled: "gray",
    }
    return (
      <Badge colorPalette={statusColors[status] || "gray"}>
        {status.toUpperCase()}
      </Badge>
    )
  }

  const renderSummaryTab = () => {
    if (summaryLoading) {
      return (
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      )
    }

    if (!summary) {
      return <Text>No data available</Text>
    }

    return (
      <Stack gap={6}>
        {/* Key Metrics */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack>
                    <FiUsers />
                    <Text>Total Students</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText>{summary.students.total}</Stat.ValueText>
                <Stat.HelpText>
                  {summary.students.households} households
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack>
                    <FiBarChart2 />
                    <Text>Active Enrollments</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText>{summary.enrollments.active}</Stat.ValueText>
                <Stat.HelpText>
                  {summary.enrollments.pending} pending
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack>
                    <FiDollarSign />
                    <Text>Monthly Revenue</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText>
                  {formatCurrency(summary.payments.monthly_revenue)}
                </Stat.ValueText>
                <Stat.HelpText>This month</Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack>
                    <FiAlertCircle />
                    <Text>Outstanding</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText color="red.500">
                  {formatCurrency(summary.payments.outstanding_amount)}
                </Stat.ValueText>
                <Stat.HelpText>
                  {summary.payments.outstanding_count} invoices
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </Grid>

        {/* Quick Actions */}
        <Card.Root>
          <Card.Header>
            <Card.Title>Quick Actions</Card.Title>
          </Card.Header>
          <Card.Body>
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={{ base: 3, md: 4 }}
            >
              <Button
                colorPalette="blue"
                onClick={() => setActiveTab("payments")}
              >
                View Outstanding Payments
              </Button>
              <Button variant="outline" onClick={() => setActiveTab("revenue")}>
                Revenue Details
              </Button>
              <Button
                variant="outline"
                onClick={() => setActiveTab("capacity")}
              >
                Class Capacity
              </Button>
            </Flex>
          </Card.Body>
        </Card.Root>
      </Stack>
    )
  }

  const renderRevenueTab = () => {
    if (revenueLoading) {
      return (
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      )
    }

    if (!revenue) {
      return <Text>No revenue data available</Text>
    }

    return (
      <Stack gap={6}>
        {/* Revenue Summary */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Total Billed</Stat.Label>
                <Stat.ValueText>
                  {formatCurrency(revenue.summary.total_billed)}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Total Collected</Stat.Label>
                <Stat.ValueText color="green.500">
                  {formatCurrency(revenue.summary.total_collected)}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Outstanding</Stat.Label>
                <Stat.ValueText color="red.500">
                  {formatCurrency(revenue.summary.total_outstanding)}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Collection Rate</Stat.Label>
                <Stat.ValueText>
                  {revenue.summary.collection_rate}%
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </Grid>

        {/* Payment Status Breakdown */}
        <Card.Root>
          <Card.Header>
            <Card.Title>Payment Status Breakdown</Card.Title>
          </Card.Header>
          <Card.Body p={0}>
            <Box overflowX="auto">
              <Table.Root variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Count
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Amount
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.entries(revenue.by_status).map(([status, data]) => (
                    <Table.Row key={status}>
                      <Table.Cell>{getStatusBadge(status)}</Table.Cell>
                      <Table.Cell textAlign="right">{data.count}</Table.Cell>
                      <Table.Cell textAlign="right">
                        {formatCurrency(data.amount)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card.Body>
        </Card.Root>

        {/* Payment Method Breakdown */}
        <Card.Root>
          <Card.Header>
            <Card.Title>By Payment Method</Card.Title>
          </Card.Header>
          <Card.Body p={0}>
            <Box overflowX="auto">
              <Table.Root variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Method</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Transactions
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Amount
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.entries(revenue.by_payment_method).map(
                    ([method, data]) => (
                      <Table.Row key={method}>
                        <Table.Cell>
                          <Text textTransform="capitalize">{method}</Text>
                        </Table.Cell>
                        <Table.Cell textAlign="right">{data.count}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {formatCurrency(data.amount)}
                        </Table.Cell>
                      </Table.Row>
                    ),
                  )}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card.Body>
        </Card.Root>
      </Stack>
    )
  }

  const renderEnrollmentsTab = () => {
    if (enrollmentsLoading) {
      return (
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      )
    }

    if (!enrollments) {
      return <Text>No enrollment data available</Text>
    }

    return (
      <Stack gap={6}>
        {/* Total Enrollments */}
        <Card.Root>
          <Card.Body>
            <Stat.Root>
              <Stat.Label>Total Enrollments</Stat.Label>
              <Stat.ValueText>{enrollments.total_enrollments}</Stat.ValueText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>

        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
          {/* By Status */}
          <Card.Root>
            <Card.Header>
              <Card.Title>By Status</Card.Title>
            </Card.Header>
            <Card.Body>
              <Table.Root variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Count
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.entries(enrollments.by_status).map(
                    ([status, count]) => (
                      <Table.Row key={status}>
                        <Table.Cell>{getStatusBadge(status)}</Table.Cell>
                        <Table.Cell textAlign="right">{count}</Table.Cell>
                      </Table.Row>
                    ),
                  )}
                </Table.Body>
              </Table.Root>
            </Card.Body>
          </Card.Root>

          {/* By Level */}
          <Card.Root>
            <Card.Header>
              <Card.Title>By Swim Level</Card.Title>
            </Card.Header>
            <Card.Body>
              <Table.Root variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Level</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Count
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.entries(enrollments.by_level).map(
                    ([level, count]) => (
                      <Table.Row key={level}>
                        <Table.Cell>
                          <Badge>{level}</Badge>
                        </Table.Cell>
                        <Table.Cell textAlign="right">{count}</Table.Cell>
                      </Table.Row>
                    ),
                  )}
                </Table.Body>
              </Table.Root>
            </Card.Body>
          </Card.Root>
        </Grid>

        {/* By Session */}
        <Card.Root>
          <Card.Header>
            <Card.Title>By Session</Card.Title>
          </Card.Header>
          <Card.Body>
            <Table.Root variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Session</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Enrollments
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {enrollments.by_session.map((session) => (
                  <Table.Row key={session.session_id}>
                    <Table.Cell>
                      <Text fontWeight="medium">{session.session_name}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {session.enrollment_count}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card.Body>
        </Card.Root>
      </Stack>
    )
  }

  const renderOutstandingTab = () => {
    if (outstandingLoading) {
      return (
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      )
    }

    if (!outstanding) {
      return <Text>No outstanding payments data available</Text>
    }

    return (
      <Stack gap={6}>
        {/* Summary */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Outstanding Invoices</Stat.Label>
                <Stat.ValueText>{outstanding.total_count}</Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Total Outstanding</Stat.Label>
                <Stat.ValueText color="red.500">
                  {formatCurrency(outstanding.total_outstanding)}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </Grid>

        {/* Outstanding Invoices Table */}
        <Card.Root>
          <Card.Header>
            <Card.Title>Outstanding Invoices</Card.Title>
            <Card.Description>
              Sorted by outstanding amount (highest first)
            </Card.Description>
          </Card.Header>
          <Card.Body p={0}>
            {outstanding.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" p={8}>
                <FiTrendingUp size={48} opacity={0.3} />
                <Text fontSize="lg" fontWeight="medium" mt={4}>
                  No outstanding payments
                </Text>
                <Text color="fg.muted" fontSize="sm">
                  All invoices are paid in full
                </Text>
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table.Root variant="outline">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Household</Table.ColumnHeader>
                      <Table.ColumnHeader>Student</Table.ColumnHeader>
                      <Table.ColumnHeader>Class</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">
                        Amount Due
                      </Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">
                        Paid
                      </Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">
                        Outstanding
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {outstanding.items.map((item) => (
                      <Table.Row key={item.invoice_id}>
                        <Table.Cell>
                          <Box>
                            <Text fontWeight="medium">
                              {item.household_name}
                            </Text>
                            <Text fontSize="sm" color="fg.muted">
                              {item.household_email}
                            </Text>
                          </Box>
                        </Table.Cell>
                        <Table.Cell>{item.student_name}</Table.Cell>
                        <Table.Cell>{item.class_name}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {formatCurrency(item.amount_due)}
                        </Table.Cell>
                        <Table.Cell textAlign="right" color="green.600">
                          {formatCurrency(item.amount_paid)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <Text fontWeight="bold" color="red.600">
                            {formatCurrency(item.outstanding_amount)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          {getStatusBadge(item.payment_status)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card.Body>
        </Card.Root>

        {/* Link to Invoices Page */}
        <Button variant="outline" onClick={() => navigate({ to: "/invoices" })}>
          Go to Invoices Page to Record Payments
        </Button>
      </Stack>
    )
  }

  const renderCapacityTab = () => {
    if (capacityLoading) {
      return (
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      )
    }

    if (!capacity) {
      return <Text>No capacity data available</Text>
    }

    return (
      <Stack gap={6}>
        {/* Capacity Summary */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Total Classes</Stat.Label>
                <Stat.ValueText>
                  {capacity.summary.total_classes}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Overall Utilization</Stat.Label>
                <Stat.ValueText>
                  {capacity.summary.overall_utilization}%
                </Stat.ValueText>
                <Stat.HelpText>
                  {capacity.summary.total_enrolled} of{" "}
                  {capacity.summary.total_capacity} spots
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Full Classes</Stat.Label>
                <Stat.ValueText color="red.500">
                  {capacity.summary.full_classes}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>Available Spots</Stat.Label>
                <Stat.ValueText color="green.500">
                  {capacity.summary.total_available}
                </Stat.ValueText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </Grid>

        {/* Classes Table */}
        <Card.Root>
          <Card.Header>
            <Card.Title>Class Capacity Details</Card.Title>
            <Card.Description>
              Sorted by utilization (fullest first)
            </Card.Description>
          </Card.Header>
          <Card.Body p={0}>
            <Box overflowX="auto">
              <Table.Root variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Class</Table.ColumnHeader>
                    <Table.ColumnHeader>Level</Table.ColumnHeader>
                    <Table.ColumnHeader>Schedule</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Enrollment
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>Utilization</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Waitlist
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {capacity.classes.map((cls) => (
                    <Table.Row key={cls.class_id}>
                      <Table.Cell>
                        <Text fontWeight="medium">{cls.class_name}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge>{cls.swim_level}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {cls.day_of_week} {cls.start_time}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Text>
                          {cls.current_enrollment} / {cls.max_students}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={2}>
                          <Progress.Root
                            value={cls.utilization_percent}
                            size="sm"
                            width="100px"
                            colorPalette={
                              cls.utilization_percent >= 100
                                ? "red"
                                : cls.utilization_percent >= 80
                                  ? "orange"
                                  : "green"
                            }
                          >
                            <Progress.Track>
                              <Progress.Range />
                            </Progress.Track>
                          </Progress.Root>
                          <Text fontSize="sm" fontWeight="medium">
                            {cls.utilization_percent}%
                          </Text>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {cls.waitlist_enabled ? (
                          cls.waitlist_count > 0 ? (
                            <Badge colorPalette="yellow">
                              {cls.waitlist_count}
                            </Badge>
                          ) : (
                            <Text fontSize="sm" color="fg.muted">
                              0
                            </Text>
                          )
                        ) : (
                          <Text fontSize="sm" color="fg.muted">
                            N/A
                          </Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card.Body>
        </Card.Root>
      </Stack>
    )
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Stack gap={6}>
        {/* Header */}
        <Box>
          <Heading size="lg">Reports & Analytics</Heading>
          <Text color="fg.muted" mt={1}>
            View business metrics, revenue, enrollments, and capacity data
          </Text>
        </Box>

        {/* Tab Navigation */}
        <Flex
          gap={2}
          borderBottomWidth="1px"
          pb={2}
          overflowX="auto"
          css={{
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Button
            variant={activeTab === "summary" ? "solid" : "ghost"}
            onClick={() => setActiveTab("summary")}
            flexShrink={0}
          >
            Summary
          </Button>
          <Button
            variant={activeTab === "revenue" ? "solid" : "ghost"}
            onClick={() => setActiveTab("revenue")}
            flexShrink={0}
          >
            Revenue
          </Button>
          <Button
            variant={activeTab === "enrollments" ? "solid" : "ghost"}
            onClick={() => setActiveTab("enrollments")}
            flexShrink={0}
          >
            Enrollments
          </Button>
          <Button
            variant={activeTab === "payments" ? "solid" : "ghost"}
            onClick={() => setActiveTab("payments")}
            flexShrink={0}
          >
            Payments
          </Button>
          <Button
            variant={activeTab === "capacity" ? "solid" : "ghost"}
            onClick={() => setActiveTab("capacity")}
            flexShrink={0}
          >
            Capacity
          </Button>
        </Flex>

        {/* Tab Content */}
        {activeTab === "summary" && renderSummaryTab()}
        {activeTab === "revenue" && renderRevenueTab()}
        {activeTab === "enrollments" && renderEnrollmentsTab()}
        {activeTab === "payments" && renderOutstandingTab()}
        {activeTab === "capacity" && renderCapacityTab()}
      </Stack>
    </Container>
  )
}
